import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, ViewChildren, QueryList } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { MediaService } from "../../services/media.service";
import { AuthService } from "../../auth.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Media } from "../../model/media";
import { Messages } from "../../model/messages";
import { Ticket } from "../../model/ticket";
import { Navigation } from "../../model/navigation";
import { NavigationHelper } from "../../services/navigation.helper";
import { environment } from "../../../environments/environment";
import { MediaDetailsComponent } from "../media-details/media-details.component";
import { HttpClient } from "@angular/common/http";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { ImagePreviewService } from "../../services/image-preview.service";

@Component({
    selector: "media-list",
    templateUrl: "./media-list.component.html",
    styleUrls: ["./media-list.component.css"],
})
export class MediaListComponent implements OnInit, OnDestroy {
    @ViewChildren(MediaDetailsComponent) detailsComponents: QueryList<MediaDetailsComponent>;

    @Input() isMobile: boolean = false;
    @Input() currentTicket: Observable<Ticket>;
    @Output() mediaSelected = new EventEmitter<number>();

    showCreateForm: boolean = false;
    medias: BehaviorSubject<Media[]> = new BehaviorSubject<Media[]>([]);
    messages: BehaviorSubject<Messages> = new BehaviorSubject(Messages.EMPTY);
    selectedMedia: Media | null = null;
    currentTicketId: number | null = null;
    loading: boolean = false;
    title: BehaviorSubject<string> = new BehaviorSubject<string>("media.title");
    thumbnailUrls = new Map<number, SafeUrl>();
    private blobUrls: string[] = [];

    constructor(
        private route: ActivatedRoute,
        private mediaService: MediaService,
        private authService: AuthService,
        private router: Router,
        private nav: NavigationHelper,
        private http: HttpClient,
        private sanitizer: DomSanitizer,
        private imagePreview: ImagePreviewService
    ) { }

    private sortedDesc(list: Media[]): Media[] {
        return [...list].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
    }

    ngOnInit(): void {
        if (this.currentTicket) {
            this.currentTicket.subscribe((ticket) => {
                if (ticket) {
                    this.currentTicketId = ticket.id;
                    const mediaList = ticket.parts?.media ?? [];
                    this.mediaService.accept(mediaList);
                    const sorted = this.sortedDesc(mediaList);
                    this.medias.next(sorted);
                    this.loadThumbnails(sorted);
                }
            });

            this.mediaService.mediaObservable.subscribe((medias) => {
                const sorted = this.sortedDesc(medias);
                this.medias.next(sorted);
                this.loadThumbnails(sorted);
            });
        }

        this.nav.fragmentId.subscribe((id) => {
            const fragmentId = Number(id);
            if (this.nav.currentFragmentTopic === "media" && (id === "-9" || fragmentId > 0)) {
                this.showCreateForm = true;
            }

            if (fragmentId > 0) {
                const found = this.medias.value.find((t) => t.id === fragmentId);
                if (found) {
                    this.selectedMedia = found;
                    if (this.nav.currentAction === "fragmentedit") {
                    }
                }
            }
        });
    }

    createMedia(): void {
        this.selectedMedia = null;
        this.showCreateForm = true;

        const newMedia = new Media(
            Navigation.NEW,
            "",
            this.currentTicketId || 0,
            "",
            0,
            false,
            null,
            null,
            null,
            null
        );

        this.mediaService.select(newMedia);

        if (this.currentTicketId) {
            const url = `tickets/${this.currentTicketId}/fragmentedit/media/${Navigation.NEW}`;
            this.router.navigate([url]).then(() => {
                this.mediaSelected.emit(Navigation.NEW);
            });
        }
    }

    isImage(contentType: string | undefined | null): boolean {
        return contentType ? contentType.startsWith("image/") : false;
    }

    startEdit(media: Media): void {
        this.selectedMedia = media;
        this.mediaService.select(media);
    }

    toggleDescription(media: Media): void {
        if (this.selectedMedia?.id === media.id) {
            this.selectedMedia = null;
        } else {
            this.selectedMedia = media;
        }
    }

    saveInline(media: Media): void {
        const activeDetail = this.detailsComponents.find(comp => comp.media.value.id === media.id);
        if (activeDetail) {
            activeDetail.triggerSave();
            this.selectedMedia = null;
        } else {
            this.saveFromTop();
        }
    }

    saveFromTop(): void {
        const topUploadComponent = this.detailsComponents.first;
        if (topUploadComponent) {
            topUploadComponent.triggerSave();
        }
    }

    getMediaThumbnailUrl(media: Media): string {
        if (!this.currentTicketId || !media || !media.id || media.id <= 0) {
            return 'assets/img/placeholder.png';
        }

        if (!media.fileInfo?.fileName) {
            return 'assets/img/placeholder.png';
        }

        return `${environment.API_BASE_URL}media/for-ticket/${this.currentTicketId}/download/${media.id}/${media.fileInfo.fileName}`;
    }

    openPreview(media: Media): void {
        const url = this.thumbnailUrls.get(media.id!);
        if (url) this.imagePreview.show(url);
    }

    private loadThumbnails(mediaList: Media[]): void {
        for (const media of mediaList) {
            if (!this.isImage(media.contentType) || !media.id || media.id <= 0 || !media.fileInfo?.fileName || !this.currentTicketId) continue;
            if (this.thumbnailUrls.has(media.id)) continue;
            const url = `${environment.API_BASE_URL}media/for-ticket/${this.currentTicketId}/download/${media.id}/${media.fileInfo.fileName}`;
            this.http.get(url, {
                responseType: 'blob',
                headers: { Authorization: `Bearer ${this.authService.token}` }
            }).subscribe({
                next: (blob) => {
                    const blobUrl = URL.createObjectURL(blob);
                    this.blobUrls.push(blobUrl);
                    this.thumbnailUrls.set(media.id!, this.sanitizer.bypassSecurityTrustUrl(blobUrl));
                },
                error: () => { /* stays as placeholder */ }
            });
        }
    }

    ngOnDestroy(): void {
        for (const url of this.blobUrls) {
            URL.revokeObjectURL(url);
        }
    }

    handleImageError(event: any): void {
        const placeholder = 'assets/img/placeholder.png';
        if (event.target.src.includes(placeholder)) {
            return;
        }
        event.target.src = placeholder;
    }

    downloadMedia(media: Media): void {
        if (!this.currentTicketId || !media.id || !media.fileInfo?.fileName) {
            return;
        }

        const url = `${environment.API_BASE_URL}media/for-ticket/${this.currentTicketId}/download/${media.id}/${media.fileInfo.fileName}`;

        const link = document.createElement('a');
        link.href = url;
        link.download = media.name || media.fileInfo.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    deleteMedia(media: Media): void {
        if (!this.currentTicketId || !media.id) return;

        const confirmDelete = confirm(`Deseja realmente excluir "${media.name}"?`);
        if (!confirmDelete) return;

        const token = this.authService.token;

        const currentList = this.medias.value;
        const updatedList = currentList.filter(m => m.id !== media.id);

        this.medias.next(updatedList);
        this.mediaService.accept(updatedList);

        this.mediaService.deleteMedia(token, this.currentTicketId, media.id)
            .subscribe({
                error: (err) => {
                    console.error("Erro ao deletar mídia", err);

                    this.medias.next(currentList);
                    this.mediaService.accept(currentList);
                }
            });
    }

    get isEditingInList(): boolean {
        return false;
    }
}